import { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, Marker, DirectionsRenderer, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { MapPin, RefreshCw, Route, User, Clock, Navigation, ChevronDown } from "lucide-react";
import AdminHeader from "@/components/common/AdminHeader";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useRoleContext } from "@/hooks/useRoleContext";
import { getBranchZoneEmployees } from "@/services/telecaller";
import { getDeliveryLocation, getDeliveryLocationHistory } from "@/services/deliveryAgent";
import { removeWhiteBg } from "@/lib/removeWhiteBg";

const LIBRARIES: ("places")[] = ["places"];
const MAP_CONTAINER = { width: "100%", height: "clamp(320px, 55vh, 700px)" };
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const ICON_SRC = "/delivery-person.jpg";

function formatUpdateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return diffSec <= 5 ? "Just now" : `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) +
    " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

interface LocationData {
  latitude: number;
  longitude: number;
  updated_at: string;
  username: string;
}

export default function TrackDeliveryPage() {
  const { canView, loading: permLoading } = useScreenPermission("location tracking");
  const { isBranchIncharge, isZoneManager, branchCode, zoneCode } = useRoleContext();

  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "").replace(/^['"]|['"]$/g, "");
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey, libraries: LIBRARIES });

  const [deliveryBoys, setDeliveryBoys] = useState<{ value: string; label: string }[]>([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [location, setLocation] = useState<LocationData | null>(null);
  const [showPath, setShowPath] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingBoys, setLoadingBoys] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [directionsResult, setDirectionsResult] = useState<google.maps.DirectionsResult | null>(null);
  const [pathStartCoord, setPathStartCoord] = useState<{ lat: number; lng: number } | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const historyIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showPathRef = useRef(false);
  const hasLocationRef = useRef(false);
  const prevRouteKeyRef = useRef("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const addressCacheRef = useRef<{ lat: number; lng: number; addr: string } | null>(null);
  const [markerIconUrl, setMarkerIconUrl] = useState(ICON_SRC);

  useEffect(() => {
    removeWhiteBg(ICON_SRC).then(setMarkerIconUrl);
  }, []);

  const deliveryPersonIcon = useCallback(() =>
    window.google
      ? { url: markerIconUrl, scaledSize: new window.google.maps.Size(52, 52), anchor: new window.google.maps.Point(26, 52) }
      : undefined,
  [markerIconUrl]);

  const handleMarkerMouseOver = useCallback(() => {
    setInfoOpen(true);
    if (!location) return;
    const cached = addressCacheRef.current;
    if (cached && Math.abs(cached.lat - location.latitude) < 0.0005 && Math.abs(cached.lng - location.longitude) < 0.0005) {
      setAddress(cached.addr);
      return;
    }
    setGeocoding(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat: location.latitude, lng: location.longitude } }, (results, status) => {
      const addr = status === "OK" && results?.[0] ? results[0].formatted_address : "Address not found";
      setAddress(addr);
      addressCacheRef.current = { lat: location.latitude, lng: location.longitude, addr };
      setGeocoding(false);
    });
  }, [location]);

  // Fetch delivery boys list
  const fetchDeliveryBoys = useCallback(async () => {
    setLoadingBoys(true);
    try {
      const params = isBranchIncharge && branchCode
        ? { Branch_Code: branchCode }
        : isZoneManager && zoneCode
          ? { Zone_Code: zoneCode }
          : undefined;
      const res = await getBranchZoneEmployees('delivery', params);
      const data: any[] = Array.isArray(res.data) ? res.data : [];
      setDeliveryBoys(data.map(e => ({ value: e.user__username, label: e.Emp_Name })));
    } catch {
      setDeliveryBoys([]);
    } finally {
      setLoadingBoys(false);
    }
  }, [isBranchIncharge, isZoneManager, branchCode, zoneCode]);

  // Fetch current location — panToLocation only on first load or manual refresh
  const fetchLocation = useCallback(async (username: string, showSpinner = false) => {
    if (!username) return;
    if (showSpinner) setLoading(true);
    try {
      const res = await getDeliveryLocation(username);
      const d = res.data as any;
      if (d) {
        const loc = Array.isArray(d) ? d[0] : d;
        if (loc?.latitude && loc?.longitude) {
          const next = {
            latitude: Number(loc.latitude),
            longitude: Number(loc.longitude),
            updated_at: loc.updated_at || new Date().toISOString(),
            username: loc.username || username,
          };
          // Pan + zoom only on first location ever received
          if (!hasLocationRef.current) {
            mapRef.current?.panTo({ lat: next.latitude, lng: next.longitude });
            mapRef.current?.setZoom(16);
            hasLocationRef.current = true;
          }
          // On manual refresh (showSpinner=true), re-center to current position
          if (showSpinner) {
            mapRef.current?.panTo({ lat: next.latitude, lng: next.longitude });
          }
          setLocation(next);
          setLastRefresh(new Date());
        }
      }
    } catch { }
    finally { if (showSpinner) setLoading(false); }
  }, []);

  // Fetch path history and request road-direction route between first & last point
  const fetchHistory = useCallback(async (username: string) => {
    if (!username) return;
    try {
      const res = await getDeliveryLocationHistory(username);
      const data = res.data as any;
      const arr: any[] = Array.isArray(data) ? data : data?.results ?? [];

      const sorted = [...arr].sort((a, b) =>
        new Date(a.created_at || a.updated_at).getTime() - new Date(b.created_at || b.updated_at).getTime()
      );

      const deduped: LocationData[] = [];
      for (const d of sorted) {
        const lat = Number(d.latitude);
        const lng = Number(d.longitude);
        if (!lat || !lng) continue;
        const prev = deduped[deduped.length - 1];
        if (prev && prev.latitude === lat && prev.longitude === lng) continue;
        deduped.push({
          latitude: lat,
          longitude: lng,
          updated_at: d.created_at || d.updated_at || new Date().toISOString(),
          username: d.username || username,
        });
      }

      if (deduped.length < 2 || !window.google) return;

      const first = deduped[0];
      const last = deduped[deduped.length - 1];
      const routeKey = `${first.latitude},${first.longitude}→${last.latitude},${last.longitude}`;
      // Only re-request directions if endpoints changed
      if (prevRouteKeyRef.current === routeKey) return;
      prevRouteKeyRef.current = routeKey;

      setPathStartCoord({ lat: first.latitude, lng: first.longitude });

      new window.google.maps.DirectionsService().route(
        {
          origin: { lat: first.latitude, lng: first.longitude },
          destination: { lat: last.latitude, lng: last.longitude },
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK" && result) setDirectionsResult(result);
        }
      );
    } catch { }
  }, []);

  // Start auto-refresh every 2s when username is selected
  const startAutoRefresh = useCallback((username: string) => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = setInterval(() => fetchLocation(username), 2000);
  }, [fetchLocation]);

  const handleSelectUser = useCallback((username: string) => {
    setSelectedUsername(username);
    setLocation(null);
    setDirectionsResult(null);
    setPathStartCoord(null);
    prevRouteKeyRef.current = "";
    hasLocationRef.current = false;
    showPathRef.current = false;
    setShowPath(false);
    if (historyIntervalRef.current) { clearInterval(historyIntervalRef.current); historyIntervalRef.current = null; }
    if (username) {
      fetchLocation(username, true);
      startAutoRefresh(username);
    } else {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    }
  }, [fetchLocation, startAutoRefresh]);

  const handleTogglePath = async () => {
    const turningOn = !showPath;
    showPathRef.current = turningOn;
    if (turningOn) {
      await fetchHistory(selectedUsername);
      if (historyIntervalRef.current) clearInterval(historyIntervalRef.current);
      historyIntervalRef.current = setInterval(() => fetchHistory(selectedUsername), 2000);
    } else {
      if (historyIntervalRef.current) { clearInterval(historyIntervalRef.current); historyIntervalRef.current = null; }
    }
    setShowPath(turningOn);
  };

  useEffect(() => {
    if (!permLoading && canView) fetchDeliveryBoys();
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (historyIntervalRef.current) clearInterval(historyIntervalRef.current);
    };
  }, [permLoading, canView]);


  if (permLoading) return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
      </div>
    </div>
  );

  if (!canView) return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="flex items-center justify-center py-32">
        <p className="text-gray-500">You don't have permission to track deliveries.</p>
      </div>
    </div>
  );
  

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AdminHeader />

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-full mx-auto flex flex-wrap items-center gap-3">
          {/* Title */}
          <div className="flex items-center gap-3 mr-auto">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Navigation className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900">Live Delivery Tracking</h1>
              <p className="text-xs text-gray-500">Real-time location of delivery personnel</p>
            </div>
          </div>

          {/* Delivery boy selector */}
          <div className="relative w-full sm:w-auto sm:min-w-[220px]">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedUsername}
              onChange={e => handleSelectUser(e.target.value)}
              disabled={loadingBoys}
              className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white appearance-none"
            >
              <option value="">{loadingBoys ? "Loading..." : "Select delivery boy"}</option>
              {deliveryBoys.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          {selectedUsername && (
            <>
              <button
                onClick={() => fetchLocation(selectedUsername, true)}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                onClick={handleTogglePath}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors border ${
                  showPath
                    ? "bg-brand-500 text-white border-brand-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                <Route className="w-4 h-4" />
                {showPath ? "Hide Path" : "Show Path"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col md:flex-row max-w-full mx-auto w-full px-4 sm:px-6 py-4 sm:py-6 gap-5 min-h-0">

        {/* Info sidebar */}
        <div className="w-full md:w-72 md:flex-shrink-0 space-y-4">

          {/* Status card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600">
              <p className="text-xs font-bold text-orange-100 uppercase tracking-wide">Current Status</p>
            </div>
            <div className="p-4 space-y-3">
              {location ? (
                <>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Delivery Boy</p>
                      <p className="text-sm font-bold text-gray-900 capitalize">{location.username}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <p className="text-sm font-bold text-green-600">Active</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Last Updated</p>
                      <p className="text-xs font-semibold text-gray-800">
                        {formatUpdateTime(location.updated_at)}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="py-6 text-center text-gray-400">
                  <Navigation className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{selectedUsername ? "Loading location…" : "Select a delivery boy"}</p>
                </div>
              )}
            </div>
          </div>

          {/* Auto-refresh info */}
          {/* {selectedUsername && (
              <div>
                {lastRefresh && (
                  <p className="text-xs text-orange-500">Last: {lastRefresh.toLocaleString()}</p>
                )}
              </div>
          )} */}

          {/* Path stats */}
          {/* {showPath && history.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Route Stats</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Points recorded</span>
                  <span className="font-bold text-gray-900">{history.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Start time</span>
                  <span className="font-semibold text-gray-700 text-xs">
                    {new Date(history[0].updated_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Latest</span>
                  <span className="font-semibold text-gray-700 text-xs">
                    {new Date(history[history.length - 1].updated_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )} */}  
        </div>

        {/* Map */}
        <div className="w-full md:flex-1 rounded-2xl overflow-hidden border border-gray-200 shadow-lg relative" style={{ height: 'clamp(320px, 55vh, 700px)' }}>
          {!selectedUsername && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-100/80 backdrop-blur-sm">
              <div className="bg-white rounded-2xl p-8 shadow-xl text-center max-w-xs">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Navigation className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-1">Select a Delivery Boy</h3>
                <p className="text-sm text-gray-500">Choose from the dropdown above to start live tracking</p>
              </div>
            </div>
          )}

          {!isLoaded ? (
            <div className="w-full flex items-center justify-center bg-gray-100" style={{ height: 'clamp(320px, 55vh, 700px)' }}>
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange-500 border-t-transparent" />
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER}
              center={DEFAULT_CENTER}
              zoom={5}
              onLoad={map => { mapRef.current = map; }}
              options={{
                mapTypeControl: false,
                fullscreenControl: true,
                streetViewControl: false,
                zoomControl: true,
                styles: [
                  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
                ],
              }}
            >
              {/* Delivery person marker */}
              {location && (
                <>
                  <Marker
                    position={{ lat: location.latitude, lng: location.longitude }}
                    icon={deliveryPersonIcon()}
                    onMouseOver={handleMarkerMouseOver}
                    onMouseOut={() => setInfoOpen(false)}
                    onClick={handleMarkerMouseOver}
                  />
                  {infoOpen && (
                    <InfoWindow
                      position={{ lat: location.latitude, lng: location.longitude }}
                      onCloseClick={() => setInfoOpen(false)}
                      options={{ pixelOffset: new window.google.maps.Size(0, -52) }}
                    >
                      <div style={{ maxWidth: 220 }}>
                        <p style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 4 }} className="capitalize">
                          {location.username}
                        </p>
                        {geocoding ? (
                          <p style={{ fontSize: 12, color: "#64748b" }}>Fetching address…</p>
                        ) : (
                          <p style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>{address}</p>
                        )}
                        <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                          {formatUpdateTime(location.updated_at)}
                        </p>
                      </div>
                    </InfoWindow>
                  )}
                </>
              )}

              {/* Road-direction route: first → last point */}
              {showPath && directionsResult && (
                <>
                  <DirectionsRenderer
                    directions={directionsResult}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: "#f97316",
                        strokeWeight: 4,
                        strokeOpacity: 0.85,
                      },
                    }}
                  />
                  {/* Start dot */}
                  {pathStartCoord && (
                    <Marker
                      position={pathStartCoord}
                      icon={{
                        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
                          `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><circle cx="10" cy="10" r="8" fill="#22c55e" stroke="white" stroke-width="2"/></svg>`
                        ),
                        scaledSize: new window.google.maps.Size(20, 20),
                        anchor: new window.google.maps.Point(10, 10),
                      }}
                      title="Start"
                    />
                  )}
                </>
              )}
            </GoogleMap>
          )}
        </div>
      </div>
    </div>
  );
}
